import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { formatAUD, useContainerWidth, EmptyState, tickLabelStyle } from './shared';

/**
 * Revenue at Risk Chart
 *
 * Histogram showing revenue distribution across price bands.
 * Reveals concentration risk — e.g. if 40% of revenue comes from
 * the top 5% of price spikes, the portfolio is heavily exposed to
 * price cap changes or demand response erosion.
 */

const BAND_COLORS = {
  '< A$0': '#ef4444',
  'A$0 – A$50': '#fcd34d',
  'A$50 – A$100': '#86efac',
  'A$100 – A$200': '#34d399',
  'A$200 – A$300': '#3b82f6',
  '> A$300': '#8b5cf6',
};

const RevenueAtRiskChart = ({ data }) => {
  const { ref, width } = useContainerWidth();

  if (!data || data.length === 0) return <EmptyState message="No revenue at risk data available" />;

  const sorted = [...data].sort((a, b) => a.band_order - b.band_order);
  // Server emits `pct_of_total` per band (see /performance/revenue-at-risk).
  // Falls back to a JS calc only when the server didn't send the field —
  // e.g. against an older backend during a rolling deploy.
  const totalRevenue = sorted.reduce((sum, d) => sum + d.revenue_aud, 0);

  const xLabels = sorted.map((d) => d.price_band);

  const revenueSeries = {
    label: 'Revenue (AUD)',
    data: sorted.map((d) => d.revenue_aud),
    color: '#D98A2B',
  };

  return (
    <Box>
      {/* Summary cards per price band */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {sorted.map((d) => {
          const pct = d.pct_of_total
            ?? (totalRevenue !== 0 ? ((d.revenue_aud / totalRevenue) * 100) : 0);
          return (
            <Paper key={d.price_band} sx={{
              px: 1.5, py: 1, flex: '1 1 120px', minWidth: 120,
              border: `1px solid ${BAND_COLORS[d.price_band] || '#e5e7eb'}20`,
              boxShadow: 'none', bgcolor: `${BAND_COLORS[d.price_band] || '#6b7280'}08`,
            }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: BAND_COLORS[d.price_band] || '#6b7280' }}>
                {d.price_band}
              </Typography>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: 'text.primary' }}>
                {pct.toFixed(1)}%
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                {formatAUD(d.revenue_aud)}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      <Box ref={ref} sx={{ width: '100%', height: 380, minWidth: 0, overflow: 'hidden' }}>
        {width > 0 && (
          <BarChart
            width={width}
            height={380}
            series={[revenueSeries]}
            xAxis={[{
              scaleType: 'band',
              data: xLabels,
              tickLabelStyle: { fontSize: 12, fill: '#5a6a7e', fontWeight: 600 },
            }]}
            yAxis={[{ valueFormatter: formatAUD, tickLabelStyle }]}
            margin={{ top: 20, right: 20, left: 80, bottom: 50 }}
            slotProps={{ legend: { hidden: true } }}
            colors={sorted.map((d) => BAND_COLORS[d.price_band] || '#6b7280')}
          />
        )}
      </Box>
    </Box>
  );
};

export default RevenueAtRiskChart;
